<script lang="ts" setup>
const { $t } = useLocalization();

const props = defineProps<{
    urls: string[];
    formImages: any;
    form: any;
    variants?: number;
    isGenerating?: boolean;
}>();

const emit = defineEmits<{
    (e: 'url-toggle', url: string): void;
}>();

const handleUrlToggle = (url: string): void =>
{
    if (!props.form.$loading && url !== 'placeholder' && url !== 'empty-slot')
    {
        emit('url-toggle', url);
    }
};

const isUrlInclude = (url: string): boolean =>
{
    return props.formImages.urlList.includes(url);
};

const displayItems = computed(() =>
{
    const items = [...props.urls];
    const remainingSlots = 4 - items.length;

    if (remainingSlots > 0)
    {
        // Zawsze pokazuj puste sloty dla pozostałych miejsc
        items.push(...Array(remainingSlots).fill('empty-slot'));
    }

    return items.slice(0, 4); // Maksymalnie 4 elementy
});

const currentGeneratingIndex = computed(() =>
{
    if (!props.isGenerating) return -1;

    // Indeks pierwszego empty-slot (gdzie aktualnie generujemy)
    return props.urls.length;
});

const getColumnClass = computed(() =>
{
    // Zawsze 2 na wiersz (2x2 grid)
    return 'col-6';
});

const getImageStyle = computed(() =>
{
    // Zawsze aspect-ratio 1:1 dla układu 2x2
    return 'aspect-ratio: 1; height: 100%;';
});
</script>

<template>
    <div class="h-100">
        <div v-if="!isGenerating && urls.length === 0" class="alert alert-light h-100 d-flex align-items-center justify-content-center rounded-3">
            <div class="user-select-none text-center text-muted p-4">
                <i class="fas fa-images display-1 mb-3 text-secondary" />
                <h5 class="fw-bold text-dark mb-2">
                    {{ $t('[[[Tu pojawią się wygenerowane obrazy]]]') }}
                </h5>
                <p class="mb-0 small">
                    {{ $t('[[[Kliknij generuj obrazy, aby je tutaj zobaczyć]]]') }}
                </p>
            </div>
        </div>
        <div v-else class="user-select-none h-100">
            <div class="row g-3 h-100">
                <div
                    v-for="(item, index) in displayItems"
                    :key="`image-${index}`"
                    :class="getColumnClass"
                >
                    <div
                        class="position-relative rounded overflow-hidden"
                        :class="{
                            'border border-primary border-3': isUrlInclude(item),
                            'cursor-pointer': !form.$loading && item !== 'placeholder' && item !== 'empty-slot',
                            'alert alert-light': item === 'empty-slot',
                        }"
                        :style="getImageStyle"
                        @click="handleUrlToggle(item)"
                    >
                        <div v-if="item === 'empty-slot'" class="h-100 d-flex flex-column align-items-center justify-content-center">
                            <div class="text-center text-muted">
                                <template v-if="isGenerating && index === currentGeneratingIndex">
                                    <i class="fas fa-spinner fa-spin fa-2x text-primary mb-2" />
                                </template>
                                <template v-else>
                                    <i class="fas fa-image opacity-25" style="font-size: 2rem;" />
                                </template>
                            </div>
                        </div>
                        <template v-else>
                            <div v-if="isUrlInclude(item)" class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50" style="z-index: 2;">
                                <i class="fas fa-check-circle text-white" style="font-size: 4rem;" />
                            </div>
                            <img :src="item" class="w-100 h-100" style="object-fit: cover;" alt="Wygenerowany obraz">
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.cursor-pointer {
    cursor: pointer;
}

.border-dashed {
    border-style: dashed !important;
}

// Przywracamy normalny margin dla alert w kontekście placeholderów
.alert.alert-light {
    margin-bottom: 0 !important;
    padding: 0 !important;
}
</style>
